// ComprobanteViewModal.js
import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Table, Spinner, Alert } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import { fetchComprobanteByNroWithDetails } from "../../reducers/comprobante_slice";
import { fetchPlanCuentas } from "../../reducers/planCuentas_slice";

import CustomPrintModal from "../UI/CustomPrintModalC";
import { formatCurrency } from "../../util/valueCalculator";
import { formatearFechaSimple } from "../../util/customFormatter";

const ComprobanteViewModal = ({ show, onHide, nrocomprobante }) => {
  const dispatch = useDispatch();

  const cuentas = useSelector((state) => state.planCuentas?.planCuentas || []);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [comprobante, setComprobante] = useState(null);
  const [detalles, setDetalles] = useState([]);

  // Print
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [printContent, setPrintContent] = useState({ head: [], body: [] });
  const [title1, setTitle1] = useState("");
  const [title2, setTitle2] = useState("");
  const [title3, setTitle3] = useState("");
  const [title4, setTitle4] = useState("");
  const [title5, setTitle5] = useState("");
  const [totalDebeStr, setTotalDebeStr] = useState("");
  const [totalHaberStr, setTotalHaberStr] = useState("");

  // ===== Totales =====
  const totales = useMemo(() => {
    const arr = Array.isArray(detalles) ? detalles : [];
    const totalDebe = arr.reduce((acc, d) => acc + Number(d?.debe || 0), 0);
    const totalHaber = arr.reduce((acc, d) => acc + Number(d?.haber || 0), 0);
    return { totalDebe, totalHaber };
  }, [detalles]);

  // ===== Carga =====
  useEffect(() => {
    const load = async () => {
      if (!show) return;

      setErrorMsg("");
      setComprobante(null);
      setDetalles([]);

      if (!nrocomprobante) return;

      try {
        setLoading(true);

        // Asegurar plan de cuentas (para nombreCuenta)
        if (!cuentas?.length) {
          await dispatch(fetchPlanCuentas()).unwrap();
        }

        const res = await dispatch(fetchComprobanteByNroWithDetails(nrocomprobante));
        const payload = res?.payload || null;

        if (!payload) {
          setErrorMsg("No se encontró el comprobante.");
          setLoading(false);
          return;
        }

        const detailsRaw = payload?.detalles || [];

        // Mapear nombre de cuenta como en tu ComprobanteForm
        const detailsWithName = detailsRaw.map((d) => {
          const cuenta = (cuentas || []).find(
            (c) => c.codigo_cuenta === d.codigo_cuenta && c.tipo_cuenta === "DETALLE"
          );
          return {
            ...d,
            nombreCuenta: cuenta ? cuenta.nombre_cuenta : (d?.nombreCuenta || "CUENTA NO ENCONTRADA"),
          };
        });

        setComprobante(payload);
        setDetalles(detailsWithName);
      } catch (e) {
        console.error("Error cargando comprobante:", e);
        setErrorMsg("No se pudo cargar el comprobante.");
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, nrocomprobante, dispatch]);

  const handleClose = () => {
    setErrorMsg("");
    setComprobante(null);
    setDetalles([]);
    setShowPrintModal(false);
    onHide && onHide();
  };

  const handlePrint = () => {
    if (!comprobante) {
      Swal.fire("Atención", "No hay comprobante para imprimir.", "info");
      return;
    }

    const filas = (detalles || []).map((d, idx) => ([
      idx + 1,
      d.codigo_cuenta || "N/D",
      d.nombreCuenta || "Cuenta no encontrada",
      d.glosa || "",
      formatCurrency(d.debe),
      formatCurrency(d.haber),
    ]));

    setPrintContent({
      head: [["", "Código", "Nombre de Cuenta", "Glosa", "Debe", "Haber"]],
      body: filas,
    });

    const fechaStr = comprobante?.fecha ? formatearFechaSimple(comprobante.fecha) : "";
    setTitle1(`COMPROBANTE ${comprobante?.estado === "BORRADOR" ? "BORRADOR" : (comprobante?.nrocomprobante || "")}`);
    setTitle2(`Fecha: ${fechaStr}`);
    setTitle3(`Beneficiario: ${comprobante?.beneficiario || ""}`);
    setTitle4(`Glosa: ${comprobante?.glosa || ""}`);
    setTitle5("");

    setTotalDebeStr(formatCurrency(totales.totalDebe));
    setTotalHaberStr(formatCurrency(totales.totalHaber));

    setShowPrintModal(true);
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} size="xl" centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            Visualizar Comprobante {nrocomprobante ? `Nro: ${nrocomprobante}` : ""}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {!nrocomprobante && (
            <Alert variant="info">No se recibió nrocomprobante.</Alert>
          )}

          {loading && (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <div className="mt-2">Cargando comprobante...</div>
            </div>
          )}

          {!loading && errorMsg && (
            <Alert variant="danger">{errorMsg}</Alert>
          )}

          {!loading && !errorMsg && comprobante && (
            <>
              {/* Encabezado */}
              <div className="d-flex flex-wrap gap-4 mb-3">
                <div>
                  <div><strong>Fecha:</strong> {comprobante?.fecha ? formatearFechaSimple(comprobante.fecha) : ""}</div>
                  <div><strong>Gestión:</strong> {comprobante?.gestion || ""}</div>
                </div>

                <div>
                  <div><strong>Tipo:</strong> {comprobante?.tipo_comprobante || ""}</div>
                  <div><strong>Estado:</strong> {comprobante?.estado || ""}</div>
                </div>

                <div style={{ minWidth: 320 }}>
                  <div><strong>Beneficiario:</strong> {comprobante?.beneficiario || ""}</div>
                </div>
              </div>

              <div className="mb-3">
                <strong>Glosa:</strong>
                <div>{comprobante?.glosa || ""}</div>
              </div>

              {/* Detalle */}
              <div className="table-responsive">
                <Table bordered hover responsive size="sm">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: 50 }}></th>
                      <th style={{ width: 140 }}>Código</th>
                      <th>Nombre de Cuenta</th>
                      <th>Glosa</th>
                      <th className="text-end" style={{ width: 140 }}>Debe</th>
                      <th className="text-end" style={{ width: 140 }}>Haber</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detalles || []).length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center">Sin detalle</td>
                      </tr>
                    ) : (
                      detalles.map((d, idx) => (
                        <tr key={d?.id || idx}>
                          <td>{idx + 1}</td>
                          <td>{d?.codigo_cuenta || ""}</td>
                          <td>{d?.nombreCuenta || ""}</td>
                          <td>{d?.glosa || ""}</td>
                          <td className="text-end">{Number(d?.debe || 0).toFixed(2)}</td>
                          <td className="text-end">{Number(d?.haber || 0).toFixed(2)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th colSpan={4} className="text-end">Totales</th>
                      <th className="text-end">{totales.totalDebe.toFixed(2)}</th>
                      <th className="text-end">{totales.totalHaber.toFixed(2)}</th>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-primary" onClick={handlePrint} disabled={!comprobante}>
            <i className="bi bi-printer"></i> Imprimir
          </Button>
          <Button variant="secondary" onClick={handleClose}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {showPrintModal && (
        <CustomPrintModal
          show={showPrintModal}
          handleClose={() => setShowPrintModal(false)}
          title1={title1}
          title2={title2}
          title3={title3}
          title4={title4}
          title5={title5}
          content={printContent}
          total1={totalDebeStr}
          total2={totalHaberStr}
        />
      )}
    </>
  );
};

export default ComprobanteViewModal;
